import { Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatNumberWithSpaces } from 'pages/calculator/MediaPlanning/common/BudgetTable/utils/formatNumberWithSpaces';
import usePlanStore from '../../../../../../store/planStore';

export function TotalBudgetSummary() {
  const { t } = useTranslation();

  const { countryForms } = usePlanStore();

  const budgetDataByCountry = Object.entries(countryForms).reduce(
    (countryAcc, [country, forms]) => {
      const digitalBudget = forms.savedCalculatedBudget;
      const offlineBudget = forms.offline?.tv?.offlineBudget;

      const sumForDigital = digitalBudget?.reduce(
        (digitalAcc, entry) => {
          if (!entry) return digitalAcc;
          return {
            currency: entry.budget.currency,
            value: digitalAcc.value + (entry.budget.value ?? 0),
          };
        },
        { currency: digitalBudget?.[0]?.budget?.currency || 'PLN', value: 0 }
      ) || { currency: 'PLN', value: 0 };

      const digitalTechCost = digitalBudget?.reduce(
        (techAcc, entry) => {
          if (!entry?.techCost) return techAcc;

          return {
            dsp: techAcc.dsp + (entry.techCost.dsp ?? 0),
            adServer: techAcc.adServer + (entry.techCost.adServer ?? 0),
          };
        },
        { dsp: 0, adServer: 0 }
      ) || { dsp: 0, adServer: 0 };

      const sumForOffline = offlineBudget
        ?.filter((entry) => entry.touchpoint !== 'tech cost')
        ?.reduce(
          (offlineAcc, entry) => {
            if (!entry?.budget) return offlineAcc;

            return {
              currency: entry.budget.currency,
              value: offlineAcc.value + (entry.budget.value ?? 0),
            };
          },
          { currency: offlineBudget?.[0]?.budget?.currency || 'PLN', value: 0 }
        ) || { currency: 'PLN', value: 0 };

      const offlineTechCost = offlineBudget
        ?.filter((entry) => entry.touchpoint === 'tech cost')
        .map((entry) => ({
          value: entry.budget.value,
          currency: entry.budget.currency,
        })) || [{ value: 0, currency: 'PLN' }];

      return {
        ...countryAcc,
        [country]: {
          sumForDigital,
          digitalTechCost,
          sumForOffline,
          offlineTechCost,
        },
      };
    },
    {} as Record<
      string,
      {
        sumForDigital: { currency: string; value: number };
        digitalTechCost: { dsp: number; adServer: number };
        sumForOffline: { currency: string; value: number };
        offlineTechCost: { value: number; currency: string }[];
      }
    >
  );

  const totalBudget = Object.values(budgetDataByCountry).reduce(
    (acc, countryData) =>
      acc +
      (countryData.sumForDigital.value ?? 0) +
      (countryData.sumForOffline.value ?? 0),
    0
  );

  const firstCurrency =
    Object.values(budgetDataByCountry)
      .map(
        (countryData) =>
          countryData.sumForDigital.currency ||
          countryData.sumForOffline.currency
      )
      .find((currency) => currency) || 'PLN';

  const totalTechCost = Object.values(budgetDataByCountry).reduce(
    (acc, countryData) =>
      acc +
      (countryData.digitalTechCost.dsp ?? 0) +
      (countryData.digitalTechCost.adServer ?? 0) +
      countryData.offlineTechCost.reduce(
        (offlineAcc, entry) => offlineAcc + (entry.value ?? 0),
        0
      ),
    0
  );

  return (
    <Grid
      container
      sx={{
        p: '24px 46px 24px 15px',
        backgroundColor: 'tableBackground.main',
      }}
    >
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography textTransform="uppercase" fontSize={18} fontWeight={700}>
          {t('mediaPlanning.totalBudget')}
        </Typography>
        <Typography fontWeight={700} fontSize={18}>
          {`${formatNumberWithSpaces(totalBudget.toFixed(0))} ${firstCurrency}`}
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography textTransform="uppercase" variant="body2" fontWeight={700}>
          {t('mediaPlanning.techCosts')}
        </Typography>
        <Typography fontWeight={700} variant="body2">
          {`${formatNumberWithSpaces(
            totalTechCost.toFixed(0)
          )} ${firstCurrency}`}
        </Typography>
      </Grid>
    </Grid>
  );
}
